// HowToUsePage.js
import React from 'react';
import { Box, Container, Typography, Paper, useTheme } from '@mui/material';

export default function HowToUsePage() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // We’ll store step data in an array for clarity.
  const steps = [
    {
      number: 1,
      title: 'Enter Your Claim',
      description: `On the main page, you’ll see a text area labeled “Enter your claim.” 
        Type or paste the statement you want to verify. Make sure it’s clear and concise 
        for the best results.`,
    },
    {
      number: 2,
      title: 'Choose Your Mode',
      description: `Toggle between “Claim” mode and “Generic” mode using the globe icon.
        • Claim Mode: Fact-checks your statement using specialized APIs.
        • Generic Mode: Uses a more open-ended AI approach to process your text.`,
    },
    {
      number: 3,
      title: 'Submit the Claim',
      description: `Click the “Check Claim” button (or “Process with Generic AI” if in 
        generic mode). XFactual sends your claim to our backend, where it’s analyzed 
        by various APIs and our DeepSeek AI.`,
    },
    {
      number: 4,
      title: 'Review the Results',
      description: `After a brief loading period, you’ll see:
        • Verdict & Confidence: Quick accuracy assessment.
        • Justification & Truth Rating: Explanation and truthfulness.
        • Wikipedia & News Articles: Additional context and sources.`,
    },
    {
      number: 5,
      title: 'Explore the History',
      description: `On the left side of the screen, you’ll find a history panel with all 
        previous claims you’ve made. Click on any claim to reload it into the input 
        field for quick reference or re-checking.`,
    },
    {
      number: 6,
      title: 'Copy or Download',
      description: `Above the results, you’ll see icons to copy the response to your 
        clipboard or download it as a file. Save or share your fact-check findings with ease.`,
    },
  ];

  // Style for the step cards (Paper or Card).
  const paperSx = {
    p: 3,
    textAlign: 'left',
    color: theme.palette.text.primary,
    backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
    boxShadow: theme.shadows[3],
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    // Remove fixed height to let them auto-size, or set one if needed.
  };

  return (
    <Box
      sx={{
        py: 8,
        backgroundColor: isDarkMode ? '#121212' : '#f5f5f5',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="md">
        {/* Page Header */}
        <Typography variant="h3" component="h1" align="center" gutterBottom>
        <img
      src="../xfactualLogo.png"
      alt="XFactual Logo"
      style={{ width: "200px", height: "auto" }}
    />
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          A step-by-step guide to get the most out of your fact-checking experience.
        </Typography>

        {/* Overview Section */}
        <Typography variant="body1" align="center" paragraph>
          XFactual is designed to help you quickly verify information by leveraging AI-driven
          fact-checking, Wikipedia lookups, and more. Follow the simple steps below to submit a
          claim, review the results, and explore additional features.
        </Typography>

        {/** Grid layout for steps: 3 columns on md+ screens, 2 on sm, 1 on xs */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: 4, // space between items
            mt: 4,
          }}
        >
          {steps.map((step) => (
            <Box key={step.number} sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* Step label above the card */}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                Step {step.number}
              </Typography>

              <Paper sx={{ ...paperSx, flexGrow: 1 }}>
                {/* Title in subtitle1, can be made blue if you wish */}
                <Typography variant="subtitle1" color='lightblue' gutterBottom>
                  {step.title}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {step.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        {/* Additional Tips */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Additional Tips
          </Typography>
          <Typography variant="body1" align="center" paragraph>
            • Use the <strong>Dark Mode</strong> toggle (top-right corner) for comfortable viewing
            at night.<br />
            • Refine your claim if the verdict seems unclear or insufficient.<br />
            • Always cross-check important information from multiple sources, especially for
            high-stakes topics.
          </Typography>
        </Box>

        {/* Call to Action */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Head back to the main page, enter your claim, and let <strong>XFactual</strong> do
            the rest. If you have any questions or need further assistance, feel free to reach out!
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}



